import React from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SAGE, WARM } from '../../theme';

/**
 * One requirement from the recipient's posting, and the sender's answer to it.
 *
 * Their posting is the index: the verbatim quote is the header, the evidence is
 * the content. Three layers (spec §2):
 *
 *   L1  requirement → claims / statement   (expanded by default)
 *   L2  claim       → context              (collapsed)
 *   L3  context     → conversation modal   (closed)
 *
 * L1 ships expanded on purpose. Collapsed by default, the page opens as a list
 * of the recipient's own posting lines with nothing underneath — which reads as
 * pasting their JD back at them.
 *
 * Treatment is deliberately clinical (spec §4). The kind is a quiet
 * right-aligned word in a fixed column, not a coloured pill, and the only
 * colour carrying meaning anywhere is the rose left rule on `candid`. There are
 * no scores, percentages, match bars or status badges: a candidate grading his
 * own fit reads as presumptuous to the person whose job that is.
 */

const KIND_LABEL = {
  evidence: 'Evidence',
  position: 'Position',
  answer: 'Answer',
  candid: 'Not a match',
};

const Chevron = ({ open, size = 18 }) => (
  <ExpandMoreIcon
    sx={{
      fontSize: size,
      color: SAGE[400],
      flexShrink: 0,
      transition: 'transform 120ms ease',
      transform: open ? 'none' : 'rotate(-90deg)',
    }}
  />
);

/** The fixed-width kind column. Quiet by design — it labels, it doesn't score. */
const KindLabel = ({ kind }) => (
  <Typography
    sx={{
      width: 74, flexShrink: 0, textAlign: 'right', mt: '3px',
      fontFamily: "'Outfit', sans-serif", fontSize: 10.5, lineHeight: 1.5,
      color: kind === 'candid' ? WARM.rose : SAGE[400],
    }}
  >
    {KIND_LABEL[kind] || KIND_LABEL.evidence}
  </Typography>
);

/** L2 — a claim, expanding to its context. */
const Claim = ({ claim, open, onToggle, onAsk, threadCount }) => {
  // A disclosure control that opens nothing is worse than no control (spec §5),
  // so a claim with no context is a plain row rather than a dead toggle.
  const hasContext = Boolean(claim.context);

  const row = (
    <>
      <Box sx={{ width: 18, flexShrink: 0, mt: '2px' }}>
        {hasContext && <Chevron open={open} size={16} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="span" sx={{ fontSize: 13.5, lineHeight: 1.6, color: SAGE[700] }}>
          {claim.claim}
        </Typography>
        {(claim.source || claim.dates) && (
          <Typography
            component="span"
            sx={{ fontSize: 11, color: SAGE[400], ml: 1, whiteSpace: 'nowrap' }}
          >
            {[claim.source, claim.dates].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>
      {threadCount > 0 && (
        <Typography sx={{ fontSize: 10.5, color: SAGE[500], flexShrink: 0, mt: '3px' }}>
          {threadCount} asked
        </Typography>
      )}
    </>
  );

  return (
    <Box sx={{ borderTop: `1px solid ${SAGE[100]}` }}>
      {hasContext ? (
        <Box
          component="button"
          onClick={onToggle}
          aria-expanded={open}
          sx={{
            width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1,
            py: 1.25, px: 0, background: 'none', border: 'none', cursor: 'pointer',
            textAlign: 'left', font: 'inherit',
          }}
        >
          {row}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1.25 }}>{row}</Box>
      )}

      <Collapse in={open && hasContext} unmountOnExit>
        <Box sx={{ pl: 3, pb: 1.5 }}>
          <Typography
            variant="body2"
            sx={{ fontSize: 13, lineHeight: 1.75, color: SAGE[600], maxWidth: '68ch' }}
          >
            {claim.context}
          </Typography>
          <Button
            size="small"
            onClick={onAsk}
            sx={{
              mt: 1.25, textTransform: 'none', fontFamily: "'Outfit', sans-serif",
              fontSize: 11.5, color: SAGE[500], border: `1px solid ${SAGE[200]}`,
              py: 0.25, px: 1.25, minWidth: 0,
            }}
          >
            Ask about this
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};

const ShareRequirement = ({
  item, open, onToggle, openClaims, onToggleClaim, onAsk, threadCounts = {},
}) => {
  const isStatement = item.kind !== 'evidence';
  const isCandid = item.kind === 'candid';

  return (
    <Box sx={{ borderBottom: `1px solid ${SAGE[200]}` }}>
      <Box
        component="button"
        onClick={onToggle}
        aria-expanded={open}
        sx={{
          width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1.25,
          py: 1.5, px: 0, background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left', font: 'inherit',
        }}
      >
        <Chevron open={open} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {item.source_label && (
            <Typography sx={{ fontSize: 11, color: SAGE[400], mb: 0.25 }}>
              {item.source_label}
            </Typography>
          )}
          {/* Their words in real typographic quotes — it has to read as theirs,
              not as ours. `verbatim: false` means generation couldn't find the
              span in the posting and fell back to a normalised restatement, so
              the quotation marks come off: attributing our paraphrase to them
              is worse than not quoting at all. */}
          <Typography
            sx={{ fontSize: 15, fontWeight: 500, lineHeight: 1.5, color: SAGE[800] }}
          >
            {item.verbatim === false ? item.quote : `“${item.quote}”`}
          </Typography>
          {item.note && (
            <Typography sx={{ fontSize: 11, color: SAGE[400], mt: 0.5 }}>{item.note}</Typography>
          )}
        </Box>
        <KindLabel kind={item.kind} />
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 3.5, pb: 1.5 }}>
          {isStatement ? (
            <Box
              sx={{
                pl: 1.75,
                borderLeft: `2px solid ${isCandid ? WARM.rose : SAGE[200]}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: 13.5, lineHeight: 1.75, color: SAGE[700], maxWidth: '68ch' }}
              >
                {item.statement}
              </Typography>
              {item.context && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, fontSize: 13, lineHeight: 1.75, color: SAGE[600], maxWidth: '68ch' }}
                >
                  {item.context}
                </Typography>
              )}
              {/* `answer` items are short yes/no logistics — nothing to discuss. */}
              {item.kind !== 'answer' && (
                <Button
                  size="small"
                  onClick={() => onAsk({ requirementId: item.id, claimId: null })}
                  sx={{
                    mt: 1.25, textTransform: 'none', fontFamily: "'Outfit', sans-serif",
                    fontSize: 11.5, color: SAGE[500], border: `1px solid ${SAGE[200]}`,
                    py: 0.25, px: 1.25, minWidth: 0,
                  }}
                >
                  Ask about this
                </Button>
              )}
            </Box>
          ) : (
            (item.claims || []).map((claim) => (
              <Claim
                key={claim.id}
                claim={claim}
                open={openClaims.has(claim.id)}
                onToggle={() => onToggleClaim(claim.id, item.id)}
                threadCount={threadCounts[claim.id] || 0}
                onAsk={() => onAsk({ requirementId: item.id, claimId: claim.id })}
              />
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ShareRequirement;
